import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import Swal from "sweetalert2";
import DataTable from "react-data-table-component";
import { Container, Card, Row, Col, Button, Spinner } from "react-bootstrap";
import { CSVLink } from "react-csv";
import "bootstrap-icons/font/bootstrap-icons.css";

import Header_Company from "../../Components/Header_Company";
import Header from "../../Components/Header_Admin";
import Header_Manager from "../../Components/Header_Manager";
import { paybridge_Url, BASE_URL } from "../../Config";
import {
  isRsaSaleContext,
  extractRetailAppReportRows,
} from "../../utils/rsaSale";
import AppLoader from "../../Components/AppLoader";
import "./Company.css";

const Company_Report = () => {
  const [reportData, setReportData] = useState([]);
  const [loading, setLoading] = useState(false);

  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [status, setStatus] = useState("All");
  const [mobileNo, setMobileNo] = useState("");
  const [authCode, setAuthCode] = useState("");
  const [agentId, setAgentId] = useState("");
  const [companyId, setCompanyId] = useState("");
  const [productSale, setProductSale] = useState("");
  const [companyName, setCompanyName] = useState("");
  const [userRole, setuserRole] = useState(false);
  const [companyDetails, setCompanyDetails] = useState([]);

  const renderWithTooltip = (value) => {
    const text = value ?? "-";
    return (
      <div
        title={text}
        style={{
          maxWidth: "150px",
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
        }}
      >
        {text}
      </div>
    );
  };

  useEffect(() => {
    const user = sessionStorage.getItem("user");
    if (!user) {
      Swal.fire("Error", "Your session has expired. Please sign in again.", "error");
      return;
    }

    const userData = JSON.parse(user);
    setCompanyId(userData.selectedCompanyId || userData.companyId || "");
    setProductSale(
      userData.selectedProductSale || userData.productSale || userData.ProductSale || ""
    );
    setCompanyName(
      userData.selectedCompanyName || userData.companyName || userData.name || ""
    );
    setuserRole(userData.userRole || "");

    if (userData.userRole === "Admin") {
      const fetchCompany = async () => {
        setLoading(true);
        try {
          const response = await axios.get(`${BASE_URL}AllCompany?status=Active`);
          const res = response.data;

          if (Array.isArray(res.companyDetails)) {
            setCompanyDetails(
              res.companyDetails.map((c) => ({
                ...c,
                applicationUserId: c.applicationUserId || c.companyProfileId,
                name: c.name || c.Name,
                productSale: c.productSale || c.ProductSale || "",
              }))
            );
          } else {
            setCompanyDetails([]);
          }
        } catch (err) {
          setCompanyDetails([]);
          Swal.fire("Error", "Failed to fetch companies", "error");
        } finally {
          setLoading(false);
        }
      };

      fetchCompany();
    }

    if (userData.userRole === "Manager") {
      const fetchAssigned = async () => {
        setLoading(true);
        try {
          const response = await axios.get(
            `${BASE_URL}AssignedCompany?ManagerId=${userData.userId}`
          );

          if (
            response.data.responseCode === "0000" &&
            Array.isArray(response.data.companyDetails)
          ) {
            const mapped = response.data.companyDetails.map((c) => ({
              applicationUserId: c.companyProfileId,
              name: c.name,
              productSale: c.productSale || c.ProductSale || "",
            }));
            setCompanyDetails(mapped);
            if (!userData.companyId && mapped[0]?.applicationUserId) {
              setCompanyId(mapped[0].applicationUserId);
              setProductSale(mapped[0].productSale || "");
              setCompanyName(mapped[0].name || "");
            }
          } else {
            setCompanyDetails([]);
          }
        } catch (err) {
          setCompanyDetails([]);
          Swal.fire("Error", "Failed to fetch assigned companies", "error");
        } finally {
          setLoading(false);
        }
      };

      fetchAssigned();
    }
  }, []);

  const fetchReport = async () => {
    setLoading(true);
    try {
      const useRsaSaleReport = isRsaSaleContext({
        productSale,
        companyName,
      });

      // RSASALE company only — RetailApp_SalesReport (does not touch paybridge)
      if (useRsaSaleReport) {
        if (!companyId) {
          Swal.fire("Error", "Please select a company.", "error");
          setReportData([]);
          return;
        }

        const response = await axios.post(
          `${BASE_URL}RetailApp_SalesReport`,
          {
            CompanyId: companyId,
            Page: 1,
            PageSize: 500,
            ...(fromDate ? { StartDate: fromDate, startDate: fromDate } : {}),
            ...(toDate ? { EndDate: toDate, endDate: toDate } : {}),
            ...(agentId ? { AgentId: agentId, agentId } : {}),
            ...(mobileNo ? { MobileNo: mobileNo, mobileNo } : {}),
          },
          { headers: { "Content-Type": "application/json" } }
        );

        const rows = extractRetailAppReportRows(response.data);
        const ok =
          response.data?.success === true ||
          response.data?.responseCode === "0000" ||
          response.data?.ResponseCode === "0000" ||
          rows.length > 0;

        if (ok && rows.length > 0) {
          setReportData(rows);
        } else if (ok) {
          Swal.fire("Info", "No records were found for the selected filters.", "info");
          setReportData([]);
        } else {
          Swal.fire(
            "Info",
            response.data?.message || "No records were found for the selected filters.",
            "info"
          );
          setReportData([]);
        }
        return;
      }

      // Existing paybridge report for all other companies
      const requestBody = {};
      if (fromDate) requestBody.startDate = fromDate;
      if (toDate) requestBody.endDate = toDate;
      if (status !== "All") requestBody.status = status;
      if (companyId) requestBody.companyId = companyId;
      if (agentId) requestBody.agentId = agentId;
      if (mobileNo) requestBody.mobileNo = mobileNo;
      if (authCode) requestBody.authCode = authCode;

      const response = await axios.post(`${paybridge_Url}report/payments`, requestBody);

      if (response.data.success) {
        setReportData(response.data.data || []);
      } else {
        Swal.fire("Info", "No records were found for the selected filters.", "info");
        setReportData([]);
      }
    } catch (error) {
      Swal.fire("Error", "Unable to fetch the report. Please try again.", "error");
      setReportData([]);
    } finally {
      setLoading(false);
    }
  };

  const isRsaSaleReport = useMemo(
    () => isRsaSaleContext({ productSale, companyName }),
    [productSale, companyName]
  );

  const paybridgeColumns = [
    { name: "Transaction Ref", selector: (row) => renderWithTooltip(row.txn_ref_no), sortable: true },
    { name: "Bill Reference", selector: (row) => renderWithTooltip(row.bill_reference), sortable: true },
    { name: "Amount", selector: (row) => renderWithTooltip(`PKR ${row.amount}`), sortable: true },
    {
      name: "Mobile No",
      selector: (row) => renderWithTooltip(row.mobile_no ?? row.pp_MobileNumber),
      sortable: true,
    },
    { name: "Status", selector: (row) => renderWithTooltip(row.status), sortable: true },
    { name: "Customer Name", selector: (row) => renderWithTooltip(row.CustomerName), sortable: true },
    { name: "Agent Name", selector: (row) => renderWithTooltip(row.agent_Name), sortable: true },
    { name: "Company Name", selector: (row) => renderWithTooltip(row.company_Name), sortable: true },
    {
      name: "Created At",
      selector: (row) =>
        renderWithTooltip(row.created_at ? new Date(row.created_at).toLocaleString() : "-"),
      sortable: true,
    },
    { name: "Response Code", selector: (row) => renderWithTooltip(row.pp_ResponseCode), sortable: true },
    { name: "Response Message", selector: (row) => renderWithTooltip(row.Static_Response), sortable: true },
  ];

  const rsaColumns = [
    {
      name: "Txn ID",
      selector: (row) => renderWithTooltip(row.txn_ref_no),
      sortable: true,
    },
    {
      name: "Customer",
      selector: (row) => renderWithTooltip(row.CustomerName),
      sortable: true,
    },
    {
      name: "Mobile",
      selector: (row) => renderWithTooltip(row.mobile_no),
      sortable: true,
    },
    {
      name: "Service",
      selector: (row) =>
        renderWithTooltip(
          row.ServiceName || row.serviceName || row.packageName || row.companyProduct || "-"
        ),
      sortable: true,
    },
    {
      name: "Payment",
      selector: (row) =>
        renderWithTooltip(row.TypeOfPayment || row.typeOfPayment || row.paymentType || "-"),
      sortable: true,
    },
    {
      name: "Agent",
      selector: (row) => renderWithTooltip(row.agent_Name),
      sortable: true,
    },
    {
      name: "Status",
      selector: (row) => renderWithTooltip(row.status),
      sortable: true,
    },
    {
      name: "Response",
      selector: (row) => renderWithTooltip(row.Static_Response || row.pp_ResponseCode),
      sortable: true,
    },
    {
      name: "Date",
      selector: (row) =>
        renderWithTooltip(
          row.created_at ? new Date(row.created_at).toLocaleString() : "-"
        ),
      sortable: true,
    },
  ];

  const columns = isRsaSaleReport ? rsaColumns : paybridgeColumns;

  const prepareCsvData = () =>
    isRsaSaleReport
      ? reportData.map((row) => ({
          "Txn ID": row.txn_ref_no ?? "-",
          Customer: row.CustomerName ?? "-",
          Mobile: row.mobile_no ?? "-",
          Service:
            row.ServiceName || row.serviceName || row.packageName || row.companyProduct || "-",
          Payment: row.TypeOfPayment || row.typeOfPayment || row.paymentType || "-",
          Agent: row.agent_Name ?? "-",
          Status: row.status ?? "-",
          Response: row.Static_Response ?? row.pp_ResponseCode ?? "-",
          Date: row.created_at ?? "-",
        }))
      : reportData.map((row) => ({
          "Transaction Ref": row.txn_ref_no ?? "-",
          "Bill Reference": row.bill_reference ?? "-",
          Amount: row.amount ?? "-",
          "Mobile No": row.mobile_no ?? "-",
          Status: row.status ?? "-",
          "Customer Name": row.CustomerName ?? "-",
          "Agent Name": row.agent_Name ?? "-",
          "Company Name": row.company_Name ?? "-",
          "Created At": row.created_at ?? "-",
          Response: row.Static_Response ?? "-",
          Responsecode: row.pp_ResponseCode ?? "-",
        }));

  return (
    <div className={`co-page${isRsaSaleReport ? " co-page--rsasale" : ""}`}>
      {userRole === "Company" ? (
        <Header_Company />
      ) : userRole === "Manager" ? (
        <Header_Manager />
      ) : (
        <Header />
      )}

      <Container fluid="xl">
        <div className="co-hero d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-2">
          <div>
            <h2>
              <i
                className={`bi ${
                  isRsaSaleReport ? "bi-clipboard2-data" : "bi-file-earmark-bar-graph"
                } me-2`}
              />
              {isRsaSaleReport ? "RSA Sales Report" : "Company Sales Report"}
            </h2>
            <p>
              {isRsaSaleReport
                ? "Filter RSA activations and export results"
                : "Filter payment records and export the results"}
            </p>
          </div>
          {isRsaSaleReport && (
            <span className="co-hero-badge">
              <i className="bi bi-patch-check-fill" />
              RSASALE
            </span>
          )}
        </div>

        <Card className="co-card">
          <Card.Header className="d-flex flex-column flex-sm-row justify-content-between align-items-sm-center gap-2">
            <h5 className="mb-0">
              <i
                className={`bi bi-funnel me-2 ${
                  isRsaSaleReport ? "" : "text-primary"
                }`}
                style={isRsaSaleReport ? { color: "#f58123" } : undefined}
              />
              Filters
            </h5>
            <span className="badge text-bg-light border">
              {reportData.length} record{reportData.length === 1 ? "" : "s"}
            </span>
          </Card.Header>

          <Card.Body>
            <div className="co-filter-bar mb-3">
              <Row className="g-3 align-items-end">
                <Col xs={12} sm={6} md={4} lg={2}>
                  <label className="form-label small fw-semibold mb-1">From Date</label>
                  <input
                    type="date"
                    className="form-control"
                    onChange={(e) => setFromDate(e.target.value)}
                  />
                </Col>
                <Col xs={12} sm={6} md={4} lg={2}>
                  <label className="form-label small fw-semibold mb-1">To Date</label>
                  <input
                    type="date"
                    className="form-control"
                    onChange={(e) => setToDate(e.target.value)}
                  />
                </Col>
                {!isRsaSaleReport && (
                  <Col xs={12} sm={6} md={4} lg={2}>
                    <label className="form-label small fw-semibold mb-1">Status</label>
                    <select className="form-select" onChange={(e) => setStatus(e.target.value)}>
                      <option>All</option>
                      <option>COMPLETED</option>
                      <option>PENDING</option>
                      <option>FAILED</option>
                    </select>
                  </Col>
                )}
                <Col xs={12} sm={6} md={4} lg={2}>
                  <label className="form-label small fw-semibold mb-1">Mobile No</label>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="03xx..."
                    onChange={(e) => setMobileNo(e.target.value)}
                  />
                </Col>
                {!isRsaSaleReport && (
                  <Col xs={12} sm={6} md={4} lg={2}>
                    <label className="form-label small fw-semibold mb-1">TID</label>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Auth / TID"
                      onChange={(e) => setAuthCode(e.target.value)}
                    />
                  </Col>
                )}

                {(userRole === "Admin" || userRole === "Manager") && (
                  <Col xs={12} sm={6} md={4} lg={2}>
                    <label className="form-label small fw-semibold mb-1">Company</label>
                    <select
                      className="form-select"
                      value={companyId}
                      onChange={(e) => {
                        const id = e.target.value;
                        setCompanyId(id);
                        const selected = companyDetails.find(
                          (c) => String(c.applicationUserId) === String(id)
                        );
                        setProductSale(selected?.productSale || selected?.ProductSale || "");
                        setCompanyName(selected?.name || selected?.Name || "");
                      }}
                    >
                      <option value="">Select Company</option>
                      {companyDetails.map((c) => (
                        <option key={c.applicationUserId} value={c.applicationUserId}>
                          {c.name}
                          {c.productSale ? ` (${c.productSale})` : ""}
                        </option>
                      ))}
                    </select>
                  </Col>
                )}

                <Col xs={12} sm={6} md={4} lg={2}>
                  <Button variant="primary" className="w-100" onClick={fetchReport} disabled={loading}>
                    {loading ? (
                      <>
                        <Spinner animation="border" size="sm" className="me-1" />
                        Loading
                      </>
                    ) : (
                      <>
                        <i className="bi bi-search me-1" />
                        {isRsaSaleReport ? "Fetch RSA Report" : "Fetch Report"}
                      </>
                    )}
                  </Button>
                </Col>
              </Row>
            </div>

            <div className={`table-responsive${loading ? " co-card-loading" : ""}`}>
              {loading && (
                <AppLoader
                  rsa={isRsaSaleReport}
                  overlay
                  message={
                    isRsaSaleReport
                      ? "Loading RSA sales report..."
                      : "Loading report..."
                  }
                />
              )}
              <DataTable
                columns={columns}
                data={reportData}
                pagination
                highlightOnHover
                responsive
                progressPending={false}
                progressComponent={
                  <AppLoader
                    rsa={isRsaSaleReport}
                    compact
                    message={
                      isRsaSaleReport
                        ? "Loading RSA sales report..."
                        : "Loading report..."
                    }
                  />
                }
                noDataComponent={
                  loading ? (
                    <AppLoader
                      rsa={isRsaSaleReport}
                      compact
                      message={
                        isRsaSaleReport
                          ? "Loading RSA sales report..."
                          : "Loading report..."
                      }
                    />
                  ) : (
                    <div className="py-4 text-muted">
                      <i className="bi bi-inbox fs-3 d-block mb-2" />
                      {isRsaSaleReport
                        ? "No RSA activations to display"
                        : "No records to display"}
                    </div>
                  )
                }
              />
            </div>

            <div className="d-flex flex-wrap gap-2 mt-3">
              <CSVLink
                data={prepareCsvData()}
                filename={
                  isRsaSaleReport ? "rsa_sales_report.csv" : "company_payments_report.csv"
                }
                className={`btn btn-success ${reportData.length === 0 ? "disabled" : ""}`}
              >
                <i className="bi bi-download me-1" />
                Download CSV
              </CSVLink>
            </div>
          </Card.Body>
        </Card>
      </Container>
    </div>
  );
};

export default Company_Report;
